import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Download,
  Layers,
  LayoutDashboard,
  ListChecks,
  Loader2,
  Workflow,
  X,
} from 'lucide-react';
import WelcomeStoryPanel from './panels/WelcomeStoryPanel';
import WelcomeTeamPanel from './panels/WelcomeTeamPanel';
import WelcomeSuccessPanel from './panels/WelcomeSuccessPanel';
import PortalBackground from '../../components/PortalBackground';
import { welcomeGlass } from './welcomeTokens';
import { getPortalEmoji } from '../../utils/portalEmojis';
import {
  listBootcampImportsPublic,
  getBootcampUnlockEnabledPublic,
  submitBootcampUnlockLead,
} from '../../services/templatesImportsService';
import {
  templateImportFileName,
  templateImportTypeNoun,
} from '../../utils/templateImportFileName';

const IMPORT_TYPE_LABELS = {
  'cost-groups': 'Cost Groups',
  schedules: 'Schedules',
  dashboards: 'Dashboards',
  todos: "To-Do's",
  workflows: 'Workflows',
};

/** Icon on each card's type pill, so the kind of file reads at a glance. */
const IMPORT_TYPE_ICONS = {
  'cost-groups': Layers,
  schedules: CalendarDays,
  dashboards: LayoutDashboard,
  todos: ListChecks,
  workflows: Workflow,
};

const TYPE_FILTER_OPTIONS = [
  { id: 'all', label: 'All types' },
  ...Object.entries(IMPORT_TYPE_LABELS).map(([id, label]) => ({ id, label })),
];

/**
 * Placeholder counts across departments; real CRM uploads augment each section.
 * `accent` drives the per-card hairline, glow and download button so every
 * department reads as one system against the animated brand background.
 */
const DEPARTMENTS = [
  {
    id: 'sales',
    name: 'Sales',
    templateCount: 5,
    accent: '#ef4444',
    titleClass: 'text-red-400',
    pillClass: 'bg-red-500/15 text-red-300 border border-red-500/30',
    downloadBtnClass:
      'bg-red-600 hover:bg-red-500 text-white focus-visible:ring-red-400',
  },
  {
    id: 'operations',
    name: 'Operations',
    templateCount: 5,
    accent: '#3b82f6',
    titleClass: 'text-blue-400',
    pillClass: 'bg-blue-500/15 text-blue-200 border border-blue-500/30',
    downloadBtnClass:
      'bg-blue-600 hover:bg-blue-500 text-white focus-visible:ring-blue-400',
  },
  {
    id: 'finance',
    name: 'Finance',
    templateCount: 4,
    accent: '#10b981',
    titleClass: 'text-emerald-400',
    pillClass: 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30',
    downloadBtnClass:
      'bg-emerald-600 hover:bg-emerald-500 text-white focus-visible:ring-emerald-400',
  },
  {
    id: 'technology',
    name: 'Technology',
    templateCount: 4,
    accent: '#a855f7',
    titleClass: 'text-purple-400',
    pillClass: 'bg-purple-500/15 text-purple-200 border border-purple-500/30',
    downloadBtnClass:
      'bg-purple-600 hover:bg-purple-500 text-white focus-visible:ring-purple-400',
  },
  {
    id: 'marketing',
    name: 'Marketing',
    templateCount: 3,
    accent: '#f97316',
    titleClass: 'text-orange-400',
    pillClass: 'bg-orange-500/15 text-orange-200 border border-orange-500/30',
    downloadBtnClass:
      'bg-orange-600 hover:bg-orange-500 text-white focus-visible:ring-orange-400',
  },
  {
    id: 'hr',
    name: 'HR',
    templateCount: 4,
    accent: '#eab308',
    titleClass: 'text-yellow-300',
    pillClass: 'bg-yellow-500/15 text-yellow-200 border border-yellow-500/30',
    downloadBtnClass:
      'bg-yellow-500 hover:bg-yellow-400 text-zinc-950 focus-visible:ring-yellow-400',
  },
];

const TOTAL_TEMPLATES = DEPARTMENTS.reduce((sum, d) => sum + d.templateCount, 0);

const HERO_STATS = [
  { value: `${TOTAL_TEMPLATES}+`, label: 'Ready-made templates' },
  { value: DEPARTMENTS.length, label: 'Departments covered' },
  { value: Object.keys(IMPORT_TYPE_LABELS).length, label: 'Import types' },
];

const BOOK_CALL_URL = 'https://calendly.com/d/ctjt-ptt-7cb/bba-discovery-call';

/** Production: Cloud Function streams file (bucket browser CORS not required). Override if URL differs. */
const BOOTCAMP_IMPORT_DOWNLOAD_URL =
  process.env.REACT_APP_BOOTCAMP_IMPORT_DOWNLOAD_URL ||
  'https://us-central1-bba-portal-894c2.cloudfunctions.net/bootcampImportDownload';

const LEAD_GATE_STORAGE_KEY = 'jtBootcampLeadGate:v1';
const LEAD_GATE_VERSION = 1;

function readLeadGateFromStorage() {
  try {
    const raw = localStorage.getItem(LEAD_GATE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.v !== LEAD_GATE_VERSION) return null;
    const name = String(data.name || '').trim();
    const email = String(data.email || '').trim();
    const phone = String(data.phone || '').trim();
    if (name.length < 2 || !email || !phone) return null;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return null;
    return { name, email, phone };
  } catch {
    return null;
  }
}

/**
 * Dev: `src/setupProxy.js` forwards this to `firebasestorage.googleapis.com` so `fetch` is
 * same-origin and works without bucket CORS. `getBlob`/`XHR` always hits Google cross-origin
 * and needs CORS — often missing for localhost.
 */
function bootcampStorageFetchUrl(downloadURL) {
  if (typeof downloadURL !== 'string' || !downloadURL) return downloadURL;
  if (process.env.NODE_ENV !== 'development') return downloadURL;
  try {
    const u = new URL(downloadURL);
    if (u.protocol !== 'https:' || u.hostname !== 'firebasestorage.googleapis.com') {
      return downloadURL;
    }
    return `/__firebase_storage__${u.pathname}${u.search}`;
  } catch {
    return downloadURL;
  }
}

/**
 * Welcome-only versions of the /start subpages: same content (imported from
 * them, not copied), restyled for this page's dark glass language.
 */
const POPUP_PANELS = {
  story: {
    title: 'Our Story',
    subtitle: 'CQC Home — the company behind BBA',
    Component: WelcomeStoryPanel,
  },
  team: {
    title: 'Our Team',
    subtitle: 'The advisors behind your program',
    Component: WelcomeTeamPanel,
  },
  success: {
    title: 'Success Stories',
    subtitle: 'Real builders, real numbers',
    Component: WelcomeSuccessPanel,
  },
};

const LEARN_MORE_CARDS = [
  {
    key: 'story',
    label: 'Our Story',
    blurb: 'How BBA got here, and who we build for.',
    emoji: '📖',
  },
  {
    key: 'team',
    label: 'Our Team',
    blurb: 'The advisors you would be working with.',
    emoji: '👥',
  },
  {
    key: 'success',
    label: 'Success Stories',
    blurb: 'Real builders, real numbers, real results.',
    emoji: '🏆',
  },
];

const TEMPLATE_TABS = [
  { id: 'all', label: 'All' },
  ...DEPARTMENTS.map((d) => ({ id: d.id, label: d.name })),
];

/** Frosted panel shared by cards, header, footer and the popups. */
const GLASS = welcomeGlass;

const WelcomeJTBootcamp = () => {
  const [unlockSettingsReady, setUnlockSettingsReady] = useState(false);
  const [leadGatePassed, setLeadGatePassed] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadFormError, setLeadFormError] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [popupKey, setPopupKey] = useState(null);
  /** `{ label, onBack }` while the open panel shows a detail view (a team member). */
  const [popupBack, setPopupBack] = useState(null);
  const popupScrollRef = useRef(null);
  const popupBackBtnRef = useRef(null);
  const popupCloseBtnRef = useRef(null);
  const [templateFilter, setTemplateFilter] = useState('all');
  const [importTypeFilter, setImportTypeFilter] = useState('all');
  const [bootcampImports, setBootcampImports] = useState([]);
  const [importsLoading, setImportsLoading] = useState(false);
  const [importsError, setImportsError] = useState(null);
  const [downloadingImportId, setDownloadingImportId] = useState(null);

  const closePopup = useCallback(() => {
    setPopupKey(null);
    setPopupBack(null);
  }, []);

  // Opening or leaving a detail view starts it at the top — otherwise a member
  // picked from low in the grid opens scrolled past their name on phones. Focus
  // follows to the header so keyboard users aren't dropped onto <body>.
  useLayoutEffect(() => {
    if (popupScrollRef.current) popupScrollRef.current.scrollTop = 0;
    (popupBack ? popupBackBtnRef : popupCloseBtnRef).current?.focus({ preventScroll: true });
  }, [popupBack]);

  /**
   * Dev: proxy same-origin fetch to Storage. Prod: HTTPS function streams file (no GCS CORS).
   */
  const downloadBootcampImport = useCallback(
    async (row) => {
      const url = row.downloadURL && String(row.downloadURL).trim();
      if (!url || !row.id || downloadingImportId) return;
      const filename = templateImportFileName(row);
      setDownloadingImportId(row.id);
      try {
        const isDev = process.env.NODE_ENV === 'development';
        const fetchUrl = isDev
          ? bootcampStorageFetchUrl(url)
          : `${BOOTCAMP_IMPORT_DOWNLOAD_URL}?${new URLSearchParams({ id: row.id, filename })}`;
        const res = await fetch(fetchUrl, { mode: 'cors', credentials: 'omit' });
        if (!res.ok) {
          throw new Error(`Download failed (${res.status})`);
        }
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        if (ct.includes('text/html')) {
          throw new Error(
            'Unexpected HTML response — dev: restart npm after setupProxy changes; prod: deploy bootcampImportDownload'
          );
        }
        const buf = await res.arrayBuffer();
        const saveBlob = new Blob([buf], { type: 'application/octet-stream' });
        const objectUrl = URL.createObjectURL(saveBlob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        console.error('Bootcamp download failed:', e);
      } finally {
        setDownloadingImportId(null);
      }
    },
    [downloadingImportId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const enabled = await getBootcampUnlockEnabledPublic();
        if (cancelled) return;
        if (!enabled) {
          setLeadGatePassed(true);
        } else {
          setLeadGatePassed(readLeadGateFromStorage() !== null);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setLeadGatePassed(readLeadGateFromStorage() !== null);
        }
      } finally {
        if (!cancelled) setUnlockSettingsReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!leadGatePassed) {
      setImportsLoading(false);
      setBootcampImports([]);
      setImportsError(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setImportsLoading(true);
      setImportsError(null);
      try {
        const rows = await listBootcampImportsPublic();
        if (!cancelled) setBootcampImports(rows);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setBootcampImports([]);
          setImportsError(e.message || 'Could not load templates.');
        }
      } finally {
        if (!cancelled) setImportsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadGatePassed]);

  const handleLeadSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLeadFormError('');
    setLeadSubmitting(true);
    const name = leadName.trim();
    const email = leadEmail.trim();
    const phone = leadPhone.trim();
    if (name.length < 2) {
      setLeadFormError('Please enter your name.');
      setLeadSubmitting(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadFormError('Please enter a valid email address.');
      setLeadSubmitting(false);
      return;
    }
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
      setLeadFormError('Please enter a valid phone number (at least 10 digits).');
      setLeadSubmitting(false);
      return;
    }
    try {
      await submitBootcampUnlockLead({ name, email, phone });
      try {
        localStorage.setItem(
          LEAD_GATE_STORAGE_KEY,
          JSON.stringify({
            v: LEAD_GATE_VERSION,
            name,
            email,
            phone,
            submittedAt: new Date().toISOString(),
          })
        );
      } catch (lsErr) {
        console.warn(lsErr);
      }
      setLeadGatePassed(true);
    } catch (err) {
      console.error(err);
      setLeadFormError(
        err?.message ||
          'Could not save your details. Check your connection and try again.'
      );
    } finally {
      setLeadSubmitting(false);
    }
  }, [leadName, leadEmail, leadPhone]);

  useEffect(() => {
    if (!popupKey) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (popupBack) popupBack.onBack();
      else closePopup();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popupKey, popupBack, closePopup]);

  const popupPanel = popupKey ? POPUP_PANELS[popupKey] : null;

  const visibleDepartments =
    templateFilter === 'all'
      ? DEPARTMENTS
      : DEPARTMENTS.filter((d) => d.id === templateFilter);

  const openContentPopup = (key) => {
    setPopupKey(key);
  };

  const showLeadGateModal = unlockSettingsReady && !leadGatePassed;
  const mainContentLocked = !unlockSettingsReady || !leadGatePassed;

  const leadFieldClass =
    'w-full rounded-xl border border-white/10 bg-zinc-950/70 pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:border-transparent';
  const leadLabelClass =
    'block text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400 mb-1.5';

  return (
    <div className="relative flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[#0d0b09] text-zinc-100">
      <PortalBackground forceDark />

      {!unlockSettingsReady && (
        <div
          className="fixed inset-0 z-[115] flex flex-col items-center justify-center gap-3 bg-[#0d0b09]/95 backdrop-blur-sm"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 className="h-8 w-8 animate-spin text-red-500" aria-hidden />
          <span className="text-sm text-zinc-500">Loading…</span>
        </div>
      )}
      {showLeadGateModal && (
        <div
          className="fixed inset-0 z-[115] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="jt-lead-gate-title"
        >
          <div className={`relative w-full max-w-md overflow-hidden rounded-2xl p-6 sm:p-8 ${GLASS}`}>
            <div
              className="pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full bg-red-600/25 blur-3xl"
              aria-hidden
            />
            <div className="relative flex flex-col items-center mb-6">
              <img
                src="/bba-emblem.svg"
                alt=""
                className="h-14 w-auto object-contain mb-4 drop-shadow-[0_0_24px_rgba(248,7,7,0.35)]"
              />
              <h2
                id="jt-lead-gate-title"
                className="text-xl font-bold text-white text-center"
              >
                Claim Your JobTread Templates
              </h2>
              <p className="mt-3 flex justify-center">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-red-300">
                  <span aria-hidden>✨</span>
                  Bootcamp Exclusive
                </span>
              </p>
            </div>
            <form onSubmit={handleLeadSubmit} className="relative space-y-4">
              {leadFormError ? (
                <p
                  className="text-sm text-red-300 bg-red-950/40 border border-red-900/50 rounded-xl px-3 py-2"
                  role="alert"
                >
                  {leadFormError}
                </p>
              ) : null}
              <div>
                <label htmlFor="jt-lead-name" className={leadLabelClass}>
                  Full name
                </label>
                <div className="relative">
                  <span
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm leading-none"
                    aria-hidden
                  >
                    👤
                  </span>
                  <input
                    id="jt-lead-name"
                    type="text"
                    autoComplete="name"
                    value={leadName}
                    onChange={(ev) => setLeadName(ev.target.value)}
                    className={leadFieldClass}
                    placeholder="Jane Builder"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="jt-lead-email" className={leadLabelClass}>
                  Email
                </label>
                <div className="relative">
                  <span
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm leading-none"
                    aria-hidden
                  >
                    ✉️
                  </span>
                  <input
                    id="jt-lead-email"
                    type="email"
                    autoComplete="email"
                    value={leadEmail}
                    onChange={(ev) => setLeadEmail(ev.target.value)}
                    className={leadFieldClass}
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="jt-lead-phone" className={leadLabelClass}>
                  Phone
                </label>
                <div className="relative">
                  <span
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm leading-none"
                    aria-hidden
                  >
                    📞
                  </span>
                  <input
                    id="jt-lead-phone"
                    type="tel"
                    autoComplete="tel"
                    value={leadPhone}
                    onChange={(ev) => setLeadPhone(ev.target.value)}
                    className={leadFieldClass}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={leadSubmitting}
                className="w-full rounded-xl bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-500 hover:to-rose-400 disabled:opacity-60 disabled:pointer-events-none text-white font-semibold py-3 text-sm shadow-lg shadow-red-900/40 transition-all hover:shadow-xl hover:shadow-red-900/50 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
              >
                {leadSubmitting ? 'Saving…' : 'Continue to templates'}
              </button>
              <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
                We save your info for our team and store a copy in this browser
                so you don&apos;t have to enter it again on this device.
              </p>
            </form>
          </div>
        </div>
      )}

      <div
        className={`relative z-10 min-h-0 flex-1 overflow-y-auto overscroll-y-contain ${mainContentLocked ? 'pointer-events-none select-none' : ''}`}
        aria-hidden={mainContentLocked}
      >
        <header
          className={`sticky top-0 z-40 border-b border-white/10 bg-[#0d0b09]/80 backdrop-blur-xl ${mainContentLocked ? 'pointer-events-auto' : ''}`}
        >
          {/* Single row at every width so the sticky bar stays short on phones.
              The JobTread lockup only fits alongside the CTA on large screens;
              below `lg` it moves into the hero instead. */}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 sm:py-4">
            <div className="flex items-center justify-between gap-3 sm:gap-6">
              <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
                <img
                  src="/bba-emblem.svg"
                  alt=""
                  className="h-9 sm:h-12 w-auto object-contain shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-[13px] sm:text-lg font-bold text-white leading-tight">
                    Builders Breakthrough Advisors
                  </p>
                  <p className="text-[9px] sm:text-[11px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-zinc-500 mt-0.5">
                    JobTread Bootcamp
                  </p>
                </div>
              </div>
              <div className="hidden lg:flex items-center gap-5 shrink-0">
                <p className="text-[11px] tracking-[0.25em] text-zinc-500 uppercase text-center whitespace-nowrap">
                  In partnership with
                </p>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                  <img
                    src="/JTLogoy.png"
                    alt=""
                    className="h-10 w-auto max-w-[140px] object-contain object-left"
                  />
                  <span className="text-lg font-bold tracking-tight text-white">
                    JobTread
                  </span>
                </div>
              </div>
              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-1.5 sm:gap-2 shrink-0 rounded-xl bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-500 hover:to-rose-400 text-white font-semibold px-3 py-2 sm:px-5 sm:py-3 text-xs sm:text-base shadow-lg shadow-red-900/40 transition-all hover:shadow-xl hover:shadow-red-900/50 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0b09]"
              >
                <span className="sm:hidden">Book a Call</span>
                <span className="hidden sm:inline">Book a Discovery Call</span>
                <ArrowRight
                  className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </a>
            </div>
          </div>
        </header>

        <main>
          <section className="relative overflow-hidden">
            <div
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(220,38,38,0.18),_transparent_58%)]"
              aria-hidden
            />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
              <p className="flex justify-center">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-red-300 backdrop-blur-sm">
                  <span aria-hidden>✨</span>
                  Bootcamp Exclusive
                </span>
              </p>
              {/* Partner lockup lives here below `lg`, where it no longer fits
                  in the sticky header. */}
              <div className="mt-6 flex flex-col items-center gap-2 lg:hidden">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  In partnership with
                </p>
                <div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                  <img
                    src="/JTLogoy.png"
                    alt=""
                    className="h-8 w-auto max-w-[120px] object-contain object-left"
                  />
                  <span className="text-base font-bold tracking-tight text-white">
                    JobTread
                  </span>
                </div>
              </div>
              <h1 className="mt-6 text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white">
                <span className="block">
                  {TOTAL_TEMPLATES}+{' '}
                  <span className="bg-gradient-to-r from-red-500 via-rose-400 to-red-500 bg-clip-text text-transparent">
                    JobTread Templates
                  </span>
                </span>
                <span className="mt-3 sm:mt-4 block">Ready To Import.</span>
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-center text-sm sm:text-base leading-relaxed text-zinc-400">
                Cost groups, schedules, dashboards, to-do lists and workflows,
                built by the BBA team and tuned for real builders. Download what
                you need and drop it straight into JobTread.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#jt-templates"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-500 hover:to-rose-400 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-red-900/40 transition-all hover:shadow-xl hover:shadow-red-900/50 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0b09]"
                >
                  <Download className="h-4 w-4" aria-hidden />
                  Browse the Templates
                </a>
                <a
                  href={BOOK_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.04] px-7 py-3 text-sm font-semibold text-zinc-200 backdrop-blur-sm transition-colors hover:bg-white/[0.09] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0b09]"
                >
                  Book a Discovery Call
                </a>
              </div>
              <dl className="mx-auto mt-12 grid max-w-2xl grid-cols-3 gap-3 sm:gap-4">
                {HERO_STATS.map((stat) => (
                  <div
                    key={stat.label}
                    className={`rounded-2xl px-3 py-5 text-center ${GLASS}`}
                  >
                    <dt className="sr-only">{stat.label}</dt>
                    <dd>
                      <span className="block text-2xl sm:text-3xl font-extrabold tabular-nums text-white">
                        {stat.value}
                      </span>
                      <span className="mt-1 block text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                        {stat.label}
                      </span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>

          <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-6 sm:pb-10">
            <h2 className="text-center text-lg sm:text-xl font-bold text-white">
              Learn More About BBA
            </h2>
            <p className="mt-2 text-center text-sm text-zinc-500">
              Three quick reads on who we are and what we do for builders.
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {LEARN_MORE_CARDS.map(({ key, label, blurb, emoji }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => openContentPopup(key)}
                  className={`group relative flex items-center gap-4 overflow-hidden rounded-2xl p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:border-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0b09] ${GLASS}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-red-500/25 bg-red-500/10 text-xl transition-colors group-hover:bg-red-500/20">
                    <span aria-hidden>{emoji}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-white">
                      {label}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-zinc-500">
                      {blurb}
                    </span>
                  </span>
                  <ArrowRight
                    className="h-4 w-4 shrink-0 text-zinc-600 transition-all group-hover:translate-x-0.5 group-hover:text-red-400"
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          </section>

          <section
            id="jt-templates"
            className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 scroll-mt-28"
          >
            <h2 className="text-center text-2xl sm:text-3xl font-bold tracking-tight text-white">
              The Template Library
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-center text-sm text-zinc-500">
              Filter by department or import type, then download the file and
              import it into your JobTread account.
            </p>

            {importsLoading && (
              <p className="mt-6 text-center text-sm text-zinc-500">
                Loading latest import templates…
              </p>
            )}
            {importsError && (
              <p className="mt-6 text-center text-sm text-red-400">{importsError}</p>
            )}

            <div className={`mt-8 mb-12 flex flex-col gap-3 rounded-2xl p-3 sm:flex-row sm:items-center sm:gap-3 min-w-0 ${GLASS}`}>
              <div
                className="flex flex-nowrap items-stretch justify-start gap-2 overflow-x-auto pb-1 -mx-1 px-1 min-w-0 sm:flex-1 sm:min-w-0 sm:pb-0"
                role="tablist"
                aria-label="Filter templates by department"
              >
                {TEMPLATE_TABS.map((tab) => {
                  const isActive = templateFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setTemplateFilter(tab.id)}
                      className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 ${
                        isActive
                          ? 'bg-gradient-to-r from-red-600 to-rose-500 text-white shadow-lg shadow-red-900/40'
                          : 'text-zinc-400 hover:text-white hover:bg-white/[0.07]'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 shrink-0 sm:border-l sm:border-white/10 sm:pl-3">
                <label
                  htmlFor="jt-bootcamp-type-filter"
                  className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500 whitespace-nowrap"
                >
                  Type
                </label>
                <select
                  id="jt-bootcamp-type-filter"
                  value={importTypeFilter}
                  onChange={(e) => setImportTypeFilter(e.target.value)}
                  className="min-w-0 max-w-[11rem] sm:max-w-[13rem] rounded-xl border border-white/10 bg-zinc-950/70 py-2 pl-2.5 pr-7 text-xs sm:text-sm text-zinc-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900"
                  aria-label="Filter by template type"
                >
                  {TYPE_FILTER_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-16">
              {visibleDepartments.map((dept) => {
                const real = bootcampImports.filter((row) => {
                  const d = row.department || '';
                  const normalized =
                    d === 'general' || !d ? 'sales' : d;
                  if (normalized !== dept.id) return false;
                  if (
                    importTypeFilter !== 'all' &&
                    row.importType !== importTypeFilter
                  ) {
                    return false;
                  }
                  return true;
                });
                const placeholders =
                  importTypeFilter === 'all'
                    ? Math.max(0, dept.templateCount - real.length)
                    : 0;

                if (real.length === 0 && placeholders === 0) return null;

                const deptEmoji = getPortalEmoji(dept.id);

                return (
                  <div key={dept.id}>
                    <div className="mb-6 flex flex-col items-center gap-2 text-center">
                      <span
                        className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold ${dept.pillClass}`}
                      >
                        <span aria-hidden>{deptEmoji}</span>
                        {dept.name}
                      </span>
                      <h3 className={`text-xl font-bold ${dept.titleClass}`}>
                        {dept.name} Templates
                      </h3>
                      <p className="text-xs uppercase tracking-[0.14em] text-zinc-600">
                        {dept.templateCount} templates
                      </p>
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
                      {real.map((row) => {
                        const typeLabel =
                          IMPORT_TYPE_LABELS[row.importType] ||
                          row.importType ||
                          'Import';
                        const TypeIcon = IMPORT_TYPE_ICONS[row.importType];
                        return (
                          <li
                            key={row.id}
                            className={`group relative flex flex-col overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:border-white/20 ${GLASS}`}
                          >
                            <span
                              className="pointer-events-none absolute inset-x-0 top-0 h-px"
                              style={{
                                background: `linear-gradient(90deg, transparent, ${dept.accent}, transparent)`,
                              }}
                              aria-hidden
                            />
                            <span
                              className="pointer-events-none absolute -right-16 -top-16 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-30"
                              style={{ background: dept.accent }}
                              aria-hidden
                            />
                            <span
                              className={`relative inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1 text-xs font-semibold ${dept.pillClass}`}
                            >
                              {TypeIcon ? (
                                <TypeIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              ) : null}
                              {typeLabel}
                            </span>
                            <div className="relative mt-3 mb-5">
                              <p className="text-lg font-semibold text-white leading-snug">
                                {row.title || row.fileName}
                              </p>
                              {row.description ? (
                                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                                  {row.description}
                                </p>
                              ) : null}
                            </div>
                            {row.downloadURL ? (
                              <button
                                type="button"
                                onClick={() => downloadBootcampImport(row)}
                                disabled={downloadingImportId !== null}
                                className={`relative mt-auto flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold shadow-lg transition-all active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900 disabled:opacity-60 disabled:pointer-events-none ${dept.downloadBtnClass}`}
                              >
                                {downloadingImportId === row.id ? (
                                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                                ) : (
                                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                                )}
                                Download {templateImportTypeNoun(row) || typeLabel} Template
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                      {Array.from({ length: placeholders }, (_, i) => (
                        <li
                          key={`${dept.id}-ph-${i}`}
                          className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] px-5 py-8 text-center"
                        >
                          <span
                            className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-lg"
                            aria-hidden
                          >
                            {deptEmoji}
                          </span>
                          <p className="text-sm font-medium text-zinc-400">
                            More {dept.name} templates
                          </p>
                          <p className="text-xs text-zinc-600">
                            Dropping soon — check back after the bootcamp.
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
            <div className={`relative overflow-hidden rounded-3xl px-6 py-10 text-center sm:px-10 sm:py-12 ${GLASS}`}>
              <div
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(220,38,38,0.16),_transparent_65%)]"
                aria-hidden
              />
              <div className="relative">
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  Want the system behind the templates?
                </h2>
                <p className="mx-auto mt-3 max-w-xl text-center text-sm leading-relaxed text-zinc-400">
                  Templates are the starting line. On a discovery call we map
                  your sales, ops, finance, tech, marketing and hiring to a
                  roadmap built for your business.
                </p>
                <a
                  href={BOOK_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-500 hover:to-rose-400 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-red-900/40 transition-all hover:shadow-xl hover:shadow-red-900/50 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0b09]"
                >
                  Book a Discovery Call
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </a>
              </div>
            </div>
          </section>
        </main>

        <footer className="border-t border-white/10 bg-[#0d0b09]/70 backdrop-blur-xl py-8 text-center text-sm text-zinc-400">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-10">
            <a
              href="tel:+19198245744"
              className="inline-flex items-center gap-2 transition-colors hover:text-white"
            >
              <span aria-hidden>📞</span>
              (919) 824-5744
            </a>
            <a
              href="https://www.buildersbreakthrough.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 transition-colors hover:text-white"
            >
              <span aria-hidden>🌐</span>
              www.buildersbreakthrough.com
            </a>
          </div>
          <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-zinc-600">
            Builders Breakthrough Advisors · In partnership with JobTread
          </p>
        </footer>
      </div>

      {popupPanel && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="jt-bootcamp-popup-title"
          onClick={closePopup}
        >
          {/* dvh, not vh: on phones 92vh is taller than the visible screen and
              pushes the header (back / close) off the top. */}
          <div
            className={`relative w-full max-w-5xl max-h-[92vh] supports-[height:100dvh]:max-h-[92dvh] rounded-2xl flex flex-col overflow-hidden ${GLASS}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center justify-center border-b border-white/10 bg-white/[0.03] px-4 py-4 shrink-0">
              {popupBack ? (
                <button
                  ref={popupBackBtnRef}
                  type="button"
                  onClick={popupBack.onBack}
                  className="absolute left-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-lg text-zinc-300 transition-colors hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                  aria-label={popupBack.label}
                  title={popupBack.label}
                >
                  <ArrowLeft className="h-5 w-5" aria-hidden />
                </button>
              ) : null}
              <div className="w-full px-10 text-center">
                <h2
                  id="jt-bootcamp-popup-title"
                  className="text-lg font-bold text-white text-center"
                >
                  {popupPanel.title}
                </h2>
                {popupPanel.subtitle ? (
                  <p className="mt-0.5 text-center text-xs text-zinc-500">
                    {popupPanel.subtitle}
                  </p>
                ) : null}
              </div>
              <button
                ref={popupCloseBtnRef}
                type="button"
                onClick={closePopup}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-lg text-zinc-400 transition-colors hover:text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div
              ref={popupScrollRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gradient-to-b from-[#1a1613] to-[#0d0b09]"
            >
              {React.createElement(popupPanel.Component, { onBackChange: setPopupBack })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WelcomeJTBootcamp;
